// **Die Plattform selbst** — Plattform, Provisioner und Feedback-API auf einen
// Build. Erst zieht der Provisioner die Images, dann setzt ein Einmal-Helfer
// den Build ein und startet den Stack neu (docs/11 12). Während des Neustarts
// antwortet die Plattform kurz nicht; die Karte fragt weiter, bis die neue
// Plattform auf dem gewählten Build antwortet.

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { get, post } from "../api/client";
import { keys } from "../api/queries";
import type { PlatformUpdateStatus } from "../api/types";
import { fmtDate } from "../lib/format";
import { ERROR, errorText, type Tone } from "../lib/labels";
import { updateLog, updateMoving, updatePhase, type UpdatePhase } from "../lib/releases";
import { Badge, Note } from "../ui/badge";
import { Card } from "../ui/card";
import { ConfirmButton } from "../ui/confirm-button";
import { toast } from "../ui/toaster";
import { BuildSelect, useBuildChoice } from "./build-select";

const PHASE: Record<UpdatePhase, [string, Tone]> = {
  idle: ["", ""],
  pulling: ["Images werden gezogen", "info"],
  helper: ["Wird eingesetzt", "info"],
  restarting: ["Startet neu", "info"],
  done: ["Aktualisiert", "ok"],
  failed: ["Gescheitert", "error"],
};

export function PlatformUpdateCard() {
  const client = useQueryClient();
  const [watching, setWatching] = useState(false);
  const status = useQuery({
    queryKey: keys.platformUpdate,
    queryFn: () => get<PlatformUpdateStatus>("/platform/update"),
    refetchInterval: (q) => (updateMoving(updatePhase(q.state.data, q.state.status === "error", watching)) ? 3000 : false),
  });
  const phase = updatePhase(status.data, status.isError, watching);
  const current = status.data?.current ?? null;
  const choice = useBuildChoice(current);
  const again = choice.value !== "" && choice.value === current;
  const log = updateLog(status.data);
  const [phaseText, tone] = PHASE[phase];
  const helper = status.data?.helper;

  const start = async () => {
    try {
      const res = await post<PlatformUpdateStatus>("/platform/update", { build: choice.value });
      setWatching(true);
      client.setQueryData(keys.platformUpdate, res);
    } catch (err) { toast(errorText(err), "error"); }
  };

  return (
    <Card
      title={<>Plattform {phaseText ? <Badge tone={tone}>{phaseText}</Badge> : null}</>}
      hint="Plattform, Provisioner und Feedback-API — ein Build für alle drei. Die Mandanten laufen dabei weiter; nur diese Seite ist kurz weg."
      foot={(
        <ConfirmButton
          className="pf-btn pf-btn--primary" disabled={!choice.value || updateMoving(phase)}
          confirmLabel="Wirklich? Die Plattform startet neu" onConfirm={start}
        >{again ? "Plattform erneut ausrollen" : "Plattform aktualisieren"}</ConfirmButton>
      )}
    >
      <div className="pf-stats">
        <div className="pf-stat"><span className="pf-stat__label">Läuft auf</span><span className="pf-stat__value pf-stat__value--code">{current ?? "unbekannt"}</span></div>
        {helper?.finished_at ? (
          <div className="pf-stat"><span className="pf-stat__label">Letzte Aktualisierung</span><span className="pf-stat__value">{fmtDate(helper.finished_at)}</span></div>
        ) : null}
      </div>
      <div className="pf-row"><BuildSelect choice={choice} /></div>
      <Note>Nicht während eines Laufs (Einrichtung, Rollout …): Der Neustart bräche ihn ab, deshalb lehnt die Plattform dann ab. Scheitert der Start, setzt der Helfer den vorigen Build wieder ein.</Note>
      {phase === "restarting" ? <Note tone="warn">Die Plattform startet neu — gleich wieder da.</Note> : null}
      {phase === "done" && watching ? (
        <Note tone="ok">
          Läuft auf {current}. <button type="button" className="pf-btn pf-btn--sm" onClick={() => window.location.reload()}>Seite neu laden</button>
        </Note>
      ) : null}
      {phase === "failed" && status.data?.pulling?.error ? <Note tone="error">{ERROR[status.data.pulling.error] ?? status.data.pulling.error}</Note> : null}
      {log.length ? (
        <details className="pf-details" open={updateMoving(phase) || phase === "failed"}>
          <summary>Protokoll ({log.length})</summary>
          <div className="pf-log">{log.join("\n")}</div>
        </details>
      ) : null}
    </Card>
  );
}
